import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2 } from 'lucide-react';
import { api, errorMessage } from '@/lib/api';
import type { Program } from '@/lib/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/field';
import { Alert, Card, CardHeader, Spinner } from '@/components/ui/misc';
import { useToast } from '@/components/ui/toast';

interface Fact {
  id: string;
  key: string;
  value: string;
}

const SUGGESTED = [
  'Portfolio size (loans)',
  'Portfolio balance',
  'Entities serviced',
  'Current subservicer',
  'Current servicing cost',
];

export function Facts({ program }: { program: Program }) {
  const base = `/api/programs/${program.id}/admin`;
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['admin', program.id, 'facts'], queryFn: () => api.get<Fact[]>(`${base}/facts`) });
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');

  const mut = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', program.id, 'facts'] }),
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (q.isLoading) return <Spinner />;
  const facts = q.data!;
  const unused = SUGGESTED.filter((s) => !facts.some((f) => f.key === s));

  return (
    <div className="flex flex-col gap-4">
      <Alert tone="info">
        Program facts are entered here, never seeded, so business-plan figures stay out of build sessions. Aggregate
        figures only: no loan, borrower or personal information.
      </Alert>
      <Card>
        <CardHeader title="Program facts" description="Key figures that reports and the Executive tab can reference." />
        {q.data!.length === 0 && <p className="px-5 py-4 text-sm text-muted">No facts yet.</p>}
        <ul>
          {facts.map((f) => (
            <li
              key={f.id}
              className="grid grid-cols-[minmax(160px,240px)_1fr_auto] items-center gap-3 border-b border-border px-5 py-2"
            >
              <Input
                aria-label="Fact name"
                className="h-8 text-[13px] font-medium"
                defaultValue={f.key}
                onBlur={(e) =>
                  e.target.value.trim() &&
                  e.target.value !== f.key &&
                  mut.mutate(() => api.patch(`${base}/facts/${f.id}`, { key: e.target.value.trim() }))
                }
              />
              <Input
                aria-label={`${f.key} value`}
                className="h-8 text-[13px]"
                defaultValue={f.value}
                onBlur={(e) =>
                  e.target.value.trim() &&
                  e.target.value !== f.value &&
                  mut.mutate(() => api.patch(`${base}/facts/${f.id}`, { value: e.target.value.trim() }))
                }
              />
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Delete ${f.key}`}
                onClick={() => mut.mutate(() => api.delete(`${base}/facts/${f.id}`))}
              >
                <Trash2 />
              </Button>
            </li>
          ))}
        </ul>
        <form
          className="grid grid-cols-[minmax(160px,240px)_1fr_auto] gap-3 px-5 py-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (!key.trim() || !value.trim()) return;
            mut.mutate(() => api.post(`${base}/facts`, { key: key.trim(), value: value.trim() }));
            setKey('');
            setValue('');
          }}
        >
          <Input
            className="h-8 text-[13px]"
            placeholder="Name"
            list="fact-suggestions"
            value={key}
            onChange={(e) => setKey(e.target.value)}
          />
          <datalist id="fact-suggestions">
            {unused.map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
          <Input
            className="h-8 text-[13px]"
            placeholder="Value"
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
          <Button type="submit" variant="secondary" size="sm">
            <Plus /> Add
          </Button>
        </form>
      </Card>
    </div>
  );
}
